/**
 * `useNodeHistory` — the data layer for one node's provenance stream
 * (B3.1 history + B3.2 timeline). TanStack Query over the already-merged
 * read endpoint:
 *
 *     GET /api/teams/{teamId}/tasks/nodes/{nodeId}/history
 *       → { node_id, events: NodeHistoryItem[] }   (chronological, asc)
 *
 * Issue #763 (Phase 3, temporal frontend). Kept deliberately separate from the
 * rendering (the clean query/view split §A7): the hook owns fetching + caching;
 * the History tab + state timeline consume `events` and never call `apiFetch`.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema types —
 * no scheduler coupling (`taskmap ⊥ scheduler`).
 *
 * Graceful degradation: the endpoint is part of base temporal infra (NOT
 * component-gated), but the backend read layer may not be deployed in a given
 * environment yet — a 404 (or any error) must NOT crash the drawer. We disable
 * retry and expose `isError`; the view renders a calm "history unavailable"
 * empty state on error and an "No activity yet" empty state on an empty stream.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

export type NodeHistoryItem = components["schemas"]["NodeHistoryItem"];
type NodeHistoryResponse = components["schemas"]["NodeHistoryResponse"];

/**
 * Which events to fetch for a node:
 *   - "node"    — this node only (the per-node provenance stream; B3.1/B3.2).
 *   - "subtree" — this node AND every node currently below it, merged
 *                 chronologically (the timeline's "This task & sub-tasks" view).
 */
export type HistoryScope = "node" | "subtree";

/** Cache key for a node's history at a given scope. Exported for tests + invalidation. */
export const NODE_HISTORY_KEY = (teamId: string, nodeId: string, scope: HistoryScope = "node") =>
  ["task-node-history", teamId, nodeId, scope] as const;

const STALE_TIME_MS = 30_000;

export interface UseNodeHistoryResult {
  /** Events, chronological (ascending `occurred_at`) as the endpoint returns. */
  readonly events: NodeHistoryItem[];
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/**
 * Fetch one node's event stream at the given `scope` ("node" default, or
 * "subtree" for the node + its descendants). `enabled` only when both ids are
 * present (i.e. a node drawer is actually open) so we never fire a request for
 * a closed drawer; the scope is part of the cache key so switching scope
 * re-fetches cleanly and both views stay cached. Degrades to an empty list on a
 * non-array / errored body.
 */
export function useNodeHistory(
  teamId: string | undefined,
  nodeId: string | undefined,
  scope: HistoryScope = "node",
): UseNodeHistoryResult {
  const query = useQuery({
    queryKey: NODE_HISTORY_KEY(teamId ?? "", nodeId ?? "", scope),
    enabled: Boolean(teamId) && Boolean(nodeId),
    staleTime: STALE_TIME_MS,
    // No retry: a not-yet-deployed endpoint should fail fast to the calm
    // "history unavailable" state rather than spinning.
    retry: false,
    queryFn: () =>
      apiFetch<NodeHistoryResponse>(
        // The "node" default omits the param so existing calls stay byte-identical.
        `/api/teams/${teamId}/tasks/nodes/${nodeId}/history${scope === "subtree" ? "?scope=subtree" : ""}`,
      ),
  });

  const events = Array.isArray(query.data?.events) ? query.data.events : [];
  return { events, isLoading: query.isLoading, isError: query.isError };
}
